import { logError } from './logger';

/**
 * 格式化日期顯示
 * @param dateString 日期字符串
 * @returns 格式化後的日期顯示
 */
export const formatTargetDate = (dateString: string): string => {
  if (!dateString) return 'No Deadline';
  
  try {
    // 檢查是否為簡單的字串描述格式
    if (typeof dateString === 'string' && !dateString.includes('T') && !dateString.includes('-')) {
      return dateString; // 如果是簡單描述，直接返回
    }
    
    const targetDate = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // 設定為同一天的0時0分，用於比較日期（不考慮時間）
    const targetDay = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const tomorrowDay = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
    
    // 日期比較 - 不再附加時間
    if (targetDay.getTime() === todayDay.getTime()) {
      return 'Today';
    } else if (targetDay.getTime() === tomorrowDay.getTime()) {
      return 'Tomorrow';
    } else {
      // 格式化日期為 MM/DD
      return `${targetDate.getMonth() + 1}/${targetDate.getDate()}`;
    }
  } catch (e) {
    logError('formatters', 'Invalid date format', dateString);
    return dateString; // 返回原始字串，以防格式錯誤
  }
};

/**
 * 格式化時間顯示為 HH:MM 格式
 * @param date 日期對象
 * @returns 格式化後的時間顯示
 */
export const formatTime = (date: Date): string => {
  return `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
};

/**
 * Duration 格式化工具集
 *
 * 統一輸出格式：
 * - 純分鐘：如 "45m"
 * - 小時+分鐘：如 "1h 30m"
 * - 純小時：如 "2h"
 */

/**
 * 核心格式化邏輯（內部使用）
 * 將分鐘數轉換為易讀格式
 * 支援天、小時、分鐘的顯示
 * @internal
 */
function formatDurationInMinutes(minutes: number): string {
  const absMinutes = Math.abs(Math.round(minutes));

  // 計算天、小時、分鐘
  const days = Math.floor(absMinutes / (24 * 60));
  const hours = Math.floor((absMinutes % (24 * 60)) / 60);
  const remainingMinutes = absMinutes % 60;

  let result = '';

  // 天數
  if (days > 0) {
    result += `${days}d`;
    if (hours > 0 || remainingMinutes > 0) result += ' ';
  }

  // 小時
  if (hours > 0) {
    result += `${hours}h`;
    if (remainingMinutes > 0) result += ' ';
  }

  // 分鐘
  if (remainingMinutes > 0 || (days === 0 && hours === 0)) {
    result += `${remainingMinutes}m`;
  }

  return result;
}

/**
 * 格式化 duration（毫秒）
 * 這是主函數，推薦使用
 *
 * @param ms 毫秒數
 * @returns 格式化的時間字符串
 *
 * @example
 * formatDuration(5400000)        // → "1h 30m"
 * formatDuration(3600000)        // → "1h"
 * formatDuration(2700000)        // → "45m"
 * formatDuration(90000000)       // → "1d 1h"
 * formatDuration(5533500000)     // → "64d 1h 15m"
 */
export function formatDuration(ms: number): string {
  const minutes = Math.round(ms / (1000 * 60));
  return formatDurationInMinutes(minutes);
}

/**
 * 格式化 duration（秒）
 *
 * @param seconds 秒數
 * @returns 格式化的時間字符串
 *
 * @example
 * formatDurationFromSeconds(5400)  // → "1h 30m"
 * formatDurationFromSeconds(3600)  // → "1h"
 * formatDurationFromSeconds(2700)  // → "45m"
 */
export function formatDurationFromSeconds(seconds: number): string {
  const ms = seconds * 1000;
  return formatDuration(ms);
}

/**
 * 格式化 duration（分鐘）
 *
 * @param minutes 分鐘數
 * @returns 格式化的時間字符串
 *
 * @example
 * formatDurationFromMinutes(90)  // → "1h 30m"
 * formatDurationFromMinutes(60)  // → "1h"
 * formatDurationFromMinutes(45)  // → "45m"
 */
export function formatDurationFromMinutes(minutes: number): string {
  return formatDurationInMinutes(minutes);
}

/**
 * 格式化 duration（小時）
 *
 * @param hours 小時數（可以是小數）
 * @returns 格式化的時間字符串
 *
 * @example
 * formatDurationFromHours(1.5)  // → "1h 30m"
 * formatDurationFromHours(2)    // → "2h"
 * formatDurationFromHours(0.75) // → "45m"
 */
export function formatDurationFromHours(hours: number): string {
  const minutes = hours * 60;
  return formatDurationInMinutes(minutes);
}

/**
 * 格式化估計時間（分鐘）成易讀格式
 * @param minutes 分鐘數或時間字符串
 * @returns 格式化後的時間顯示 (例: 90 -> 1h 30m)
 * @deprecated 建議使用 formatDurationFromMinutes，命名更清晰
 */
export const formatEstimatedTime = (minutes?: number | string): string => {
  // 處理 undefined, null 或 0 的情況
  if (minutes === undefined || minutes === null) {
    return '';
  }

  // 確保處理的是數字
  let numericMinutes: number;
  if (typeof minutes === 'string') {
    // 檢查是否已經是格式化的時間字符串
    if (/^\d+[hmd]\s*(\d+[hmd])?$/.test(minutes)) {
      return minutes; // 已經是格式化的時間，直接返回
    }

    // 嘗試從字符串提取數字
    const numericPart = minutes.replace(/[^\d.-]/g, '');
    numericMinutes = parseFloat(numericPart);
  } else {
    numericMinutes = minutes;
  }

  // 確保是有效數字
  if (isNaN(numericMinutes)) {
    return String(minutes);
  }

  // 使用核心格式化函數（避免代碼重複）
  return formatDurationInMinutes(numericMinutes);
};

/**
 * 格式化類別顯示名稱
 * @param category 類別代碼
 * @returns 格式化後的類別名稱
 */
export const formatCategoryName = (category: string): string => {
  const CATEGORY_MAP: Record<string, string> = {
    'work': 'Work',
    'personal': 'Personal',
    'family': 'Family',
    'health': 'Health',
    'social': 'Social',
    'untitled': 'Untitled'
  };
  
  return CATEGORY_MAP[category] || category;
};

/**
 * 格式化主題顯示名稱 (為了向後兼容)
 * @param theme 主題代碼
 * @returns 格式化後的主題名稱
 * @deprecated 使用 formatCategoryName 代替
 */
export const formatThemeName = (theme: string): string => {
  return formatCategoryName(theme);
};